/** 비교 뷰 본문: 헤더, IXD 사이드바, DSD/IXD 프리뷰 패널, 셀 네비게이터 모달 */

import {
  DSDSubChunkInfo,
  DsdHeaderTag,
  IxdChunk,
  MatchedIxdCell,
} from "@/apis/compare/route";
import type { PeriodLabels, PeriodType } from "@/apis/compare/types";
import type { IXDPreviewResponse } from "@/apis/ixd-editor/types";
import { MatchNavigation } from "@/app/ixd-dsd-compare/types";
import { CompareHeader } from "@/components/ixd-dsd-compare/compare-header";
import { buildMemberAxisOptions } from "./dsd-region-views";
import IxdPreviewPanel from "@/components/ixd-dsd-compare/ixd-preview-panel";
import type { IxdPreviewRendererRef } from "@/components/ixd-preview";
import { MatchNavigationPopover } from "@/components/ixd-dsd-compare/match-navigation-popover";
import {
  PreviewPanel,
  type PreviewPanelHandle,
} from "@/components/ixd-dsd-compare/preview-panel";
import type {
  DsdDlRegionView,
  DsdLineItemRegionView,
  DsdMemberRegionView,
  DsdPeriodRegionView,
  DsdPositionReview,
} from "@/components/ixd-dsd-compare/dsd-preview-renderer";
import { useCompareUrlState } from "@/hooks/ixd-dsd-compare/use-compare-url-state";
import { useDsdIxdCounterpart } from "@/hooks/ixd-dsd-compare/use-dsd-ixd-counterpart";
import { useIxdDsdCounterpart } from "@/hooks/ixd-dsd-compare/use-ixd-dsd-counterpart";
import {
  buildCellIdAliasesFromTaggedValue,
  buildTableLabelMap,
  normalizeCellId,
  type CellIdentityLocation,
} from "@/utils/ixd-cell-model";
import {
  useIxdEditSession,
  useUnsavedChangesGuard,
} from "@/hooks/ixd-edit-session";
import queries from "@/queries";
import { useIxdDsdCompareStore } from "@/stores/ixd-dsd-compare";
import { collectMatchedIxdCellIds } from "@/utils/ixd-dsd-compare/build-ixd-by-dsd-cell-id";
import type { FactLabels } from "@/utils/ixd-dsd-compare/header-counterpart";
import {
  BetaFeatureOnly,
  isBetaFeatureEnabled,
} from "@/components/beta-feature";
import {
  blockWhileReviewing,
  PositionReviewCard,
  PositionReviewStatus,
  PositionReviewToggle,
} from "./position-check/position-check";
import { usePositionCheck } from "./position-check/use-position-check";
import { summarizeValuePresence } from "./position-check/value-presence";
import { afterScrollSettles } from "@/utils/ixd-dsd-compare/focus-utils";
import { getPeriodLabel } from "@/utils/ixd-editor/period-labels";
import { isValueCell } from "@/utils/ixd-cell-model";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { RefObject, useCallback, useMemo, useRef, useState } from "react";

/** useNavigation 훅에서 CompareContentView가 사용하는 메서드만 노출 (PreviewPanel onValueClick/onCellClick 시그니처와 일치) */
export interface CompareContentNavigation {
  handleMatchedValueClick: (
    value: string,
    matchedIxdCells: MatchedIxdCell[],
    mostProperCellId?: string | null,
    /**
     * 포커싱 대상을 좁힐 DL·기간. 매칭 클릭은 **정본 셀이 실제로 속한** 위치이고,
     * 정본이 없거나 그 축을 모르면 클릭한 DSD 셀의 배정이다.
     */
    dlViewId?: string | null,
    period?: string | null,
  ) => void;
  handleShouldTagCellClick: (
    value: string,
    inferredCellId: string | null,
    dlViewId?: string | null,
    period?: string | null,
  ) => void;
  /** 우클릭 데이터 셀의 추론값 포커싱 — 배정된 DL 밖으로 나가지 않는다 */
  handleInferredCellFocus: (
    inferredCellId: string,
    value: string,
    dlViewId?: string | null,
    period?: string | null,
  ) => void;
  handleNavigateToIndex: (index: number) => void;
  handleNavigationClose: () => void;
  handleNavigationDismiss: () => void;
  handleHeaderFocus: (tag: {
    lineItem?: string | null;
    period?: string | null;
    member?: string | null;
  }) => boolean;
  clearIxdFocus?: () => void;
}

export interface CompareContentViewProps {
  // Refs
  ixdRendererRef: RefObject<IxdPreviewRendererRef>;

  // 파일 / 헤더
  dsdFile: File | null;
  ixdFile: File | null;

  // IXD 프리뷰
  currentIxdChunks: IxdChunk[];
  activeIxdChunk: IxdChunk | null;

  // DSD 프리뷰
  activeChunk: {
    id: string;
    title: string;
    subChunks: DSDSubChunkInfo[];
  } | null;
  dsdPreview: {
    chunks: { id: string; title: string; subChunks: DSDSubChunkInfo[] }[];
    companyName: string | null;
    reportBase: string | null;
  } | null;
  /** 헤더 셀 dsdId → 매핑된 태그 (우클릭 태그 popover 용) */
  headerTagsByDsdId?: Map<string, DsdHeaderTag> | null;
  dlRegions?: DsdDlRegionView[] | null;
  periodRegions?: DsdPeriodRegionView[] | null;
  lineItemRegions?: DsdLineItemRegionView[] | null;
  memberRegions?: DsdMemberRegionView[] | null;
  isSyncScroll: boolean;

  // 네비게이션
  navigation: CompareContentNavigation;
  matchNavigation: MatchNavigation | null;
  focusedDsdResetKey?: number;
  onCompareFocusClose?: () => void;

  // 로딩
  isChunkLoading?: boolean;
  /** chunk 전환 시 백그라운드 fetching 중 (keepPreviousData 사용 시) */
  isChunkFetching?: boolean;
  onFocusIdentityLocation?: (location: CellIdentityLocation) => boolean | void;

  // HTML 다운로드
  onDownloadHtml?: () => void;
  isDownloadingHtml?: boolean;
}

export const CompareContentView = ({ ...props }: CompareContentViewProps) => {
  const ixdReportId = useIxdDsdCompareStore((s) => s.ixdReportId);

  if (!ixdReportId) {
    return (
      <CompareContentViewBody
        {...props}
        ixdReportId={null}
        editorPreview={undefined}
        isLinkageLoading={false}
        isLinkageError={false}
      />
    );
  }

  return (
    <CompareContentViewWithEditorPreview {...props} ixdReportId={ixdReportId} />
  );
};

const CompareContentViewWithEditorPreview = ({
  ixdReportId,
  ...props
}: CompareContentViewProps & { ixdReportId: string }) => {
  // 편집 세션 cell index의 입력은 전체 IXD editor preview에서 가져온다.
  // 화면 chunk가 부분 응답이어도 저장 검증은 report 전체 dlView.values를 기준으로 한다.
  const {
    data: editorPreview,
    isPending,
    isError,
  } = useQuery({
    ...queries.ixdEditor(ixdReportId)._ctx.preview(),
    // staleTime/structuralSharing 은 factory(preview)에서 상속한다. 여기서
    // staleTime 을 덮어쓰면 포커스 refetch 가 되살아나 큰 파일에서 열거 한계에
    // 걸리므로 override 하지 않는다.
    gcTime: 30 * 60 * 1000,
  });

  return (
    <CompareContentViewBody
      {...props}
      ixdReportId={ixdReportId}
      editorPreview={editorPreview}
      isLinkageLoading={isPending}
      isLinkageError={isError}
    />
  );
};

const NO_SUB_CHUNKS: DSDSubChunkInfo[] = [];

const ChunkLoadingIndicator = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3 rounded-[10px] border border-line-normal bg-background-base px-5 py-3 shadow-xl">
    <Loader2 className="w-5 h-5 animate-spin text-primary-base" />
    <span className="label-1-normal-medium text-label-normal">{label}</span>
  </div>
);

interface CompareContentViewBodyProps extends CompareContentViewProps {
  ixdReportId: string | null;
  editorPreview: IXDPreviewResponse | undefined;
  /** editorPreview(연동 판정 데이터) 도착 전 — dog-ear 가 아직 계산 중임을 표시. */
  isLinkageLoading: boolean;
  /** editorPreview 조회 실패. retry 소진 + staleTime: Infinity 라 자동 회복이 없다. */
  isLinkageError: boolean;
}

const CompareContentViewBody = ({
  ixdRendererRef,
  dsdFile,
  ixdFile,
  currentIxdChunks,
  activeIxdChunk,
  activeChunk,
  dsdPreview,
  headerTagsByDsdId,
  dlRegions,
  periodRegions,
  lineItemRegions,
  memberRegions,
  navigation,
  matchNavigation,
  focusedDsdResetKey,
  onCompareFocusClose,
  isChunkLoading,
  isChunkFetching,
  onFocusIdentityLocation,
  onDownloadHtml,
  isDownloadingHtml,
  ixdReportId,
  editorPreview,
  isLinkageLoading,
  isLinkageError,
}: CompareContentViewBodyProps) => {
  const selectedFsType = useIxdDsdCompareStore((s) => s.selectedFsType);
  const { comparisonId } = useCompareUrlState();
  const isIxdEditMode = useIxdDsdCompareStore((s) => s.isIxdEditMode);
  const setIsIxdEditMode = useIxdDsdCompareStore((s) => s.setIsIxdEditMode);

  const queryClient = useQueryClient();

  const editSession = useIxdEditSession({
    reportId: ixdReportId,
    fsType: selectedFsType,
    consolidatedChunks: editorPreview?.consolidatedChunks,
    separateChunks: editorPreview?.separateChunks,
    onSaveSuccess: useCallback(async () => {
      if (!comparisonId) return;
      const chunkQueryKey = queries.comparison(comparisonId)._ctx.chunk._def;
      // 로컬 캐시를 선반영하지 않고 서버가 재계산한 chunk 응답을 기다린다.
      await queryClient.invalidateQueries(
        {
          queryKey: chunkQueryKey,
          refetchType: "active",
        },
        { throwOnError: true },
      );
    }, [comparisonId, queryClient]),
  });

  // 재조회 중인 변경은 서버 저장이 이미 끝났으므로 이탈 경고 대상이 아니다.
  useUnsavedChangesGuard(editSession.hasUnguardedChanges);

  // editorPreview는 cell index의 입력. 도착 전에 편집 허용하면 저장 시 변경사항이
  // index 매칭 실패로 모두 누락되어 데이터 손실이 발생할 수 있다.
  const canEdit = !!ixdReportId && !!editorPreview;
  const editable = canEdit && isIxdEditMode;
  // 편집이 막힌 이유가 셋이라 뭉뚱그리면 사용자가 기다려야 할지, 새로고침해야 할지,
  // 포기해야 할지 알 수 없다. 공시본(.zip) 대사는 서버가 IXD report 를 만들지 않아
  // 영구적이고, preview 미도착은 잠시 뒤 풀리며, 조회 실패는 retry 를 다 쓴 뒤라
  // 기다려도 풀리지 않는다(staleTime: Infinity — 자동 refetch 없음).
  const editDisabledReason = !ixdReportId
    ? "DART 공시 XBRL로 만든 대사는 편집할 수 없습니다"
    : isLinkageError
      ? "IXD 편집 데이터를 불러오지 못했습니다. 새로고침 후 다시 시도해주세요"
      : "IXD 편집 데이터를 불러오는 중입니다";

  const previewPanelRef = useRef<PreviewPanelHandle | null>(null);

  // 청크 전환·편집 토글 시 양방향 선택을 비운다 — stale 선택이 반대편을 잘못
  // dim/강조하지 않도록.
  //
  // `activeIxdChunkId` 는 일부러 뺀다(뷰어는 반대로 그걸 키로 쓴다). IXD 렌더러는
  // 매핑된 청크를 **전부 한 스크롤에 함께** 그리고 activeIxdChunkId 는 선택 커서일
  // 뿐이라(`ixd-preview-renderer.tsx` 의 `chunks.map`), 콤보로 청크를 넘겨도 고른
  // 셀은 화면에 그대로 남는다. 여기서 비우면 IXD 선택 강조는 남은 채 DSD dim 만
  // 풀려 두 패널이 오히려 어긋난다.
  const selectionResetKey = `${activeChunk?.id}:${isIxdEditMode}`;

  // IXD 선택 → DSD 스크롤 + dim. 다운로드 뷰어와 같은 훅을 쓴다(동선이 갈리면 안 된다).
  const { onIxdCellIdsChange, counterpartDsdIds } = useIxdDsdCounterpart({
    subChunks: activeChunk?.subChunks,
    previewPanelRef,
    resetKey: selectionResetKey,
    disabled: editable,
  });

  // DSD 드래그 선택 → IXD 스크롤 + 외곽선.
  const { onDsdCellIdsChange, ixdCounterpart } = useDsdIxdCounterpart({
    subChunks: activeChunk?.subChunks,
    ixdRendererRef,
    resetKey: selectionResetKey,
    disabled: editable,
  });

  // 어떤 DSD 셀과도 매칭된 IXD 원본 cellId 전체 집합. 이 목록에 없는 IXD 값 셀을
  // "매칭 DSD 없음"으로 보고 IXD 패널에서 빨간색으로 상시 강조한다.
  const matchedIxdCellIds = useMemo(
    () => collectMatchedIxdCellIds(activeChunk?.subChunks),
    [activeChunk?.subChunks],
  );

  // member 검토 축 — 툴바가 고르고 렌더러가 그 축만 셀에 싣는다. 한 셀이 축마다
  // member 를 하나씩 갖는데 면색·이름표 채널은 하나뿐이라 축을 밖에서 정해야 한다.
  const [reviewAxisId, setReviewAxisId] = useState<string | null>(null);
  const memberAxes = useMemo(
    () => buildMemberAxisOptions(memberRegions),
    [memberRegions],
  );

  // 편집 모드에서는 끈다 — 값을 고치면 재계산이 끝날 때까지 라벨이 이전 대응을 보여 준다.
  // 베타가 꺼져 있어도 끈다 — 라벨 사전이 없으면 값 위치 검토 훅이 단축키·표시를 모두 걷는다.
  // 문구는 IXD 패널이 보여 주는 청크의 머리 텍스트에서 가져온다(없으면 id 그대로).
  const counterpartFactLabels = useMemo<FactLabels | null>(() => {
    if (isIxdEditMode || !isBetaFeatureEnabled()) return null;
    const textById = new Map<string, string>();
    const periodLabelsByDlViewId = new Map<string, PeriodLabels | null>();
    for (const chunk of currentIxdChunks) {
      for (const dlView of chunk.dlViews) {
        if (dlView.id)
          periodLabelsByDlViewId.set(dlView.id, dlView.periodLabels ?? null);
        for (const rows of Object.values(dlView.table ?? {})) {
          for (const [id, label] of buildTableLabelMap(rows)) {
            if (!textById.has(id)) textById.set(id, label);
          }
        }
      }
    }
    return {
      lineItem: (id) => textById.get(id) ?? id,
      member: (id) => textById.get(id) ?? id,
      period: (periodType, dlViewId) =>
        getPeriodLabel(
          periodType as PeriodType,
          dlViewId ? periodLabelsByDlViewId.get(dlViewId) : null,
        ),
    };
  }, [isIxdEditMode, currentIxdChunks]);

  // 값 위치 검토 — 도구줄에서 켜고, 켜면 지금 줄 옆에 검토 카드가 붙는다.
  const subChunksForReview = activeChunk?.subChunks ?? NO_SUB_CHUNKS;
  const scrollDsdToCell = useCallback(
    (dsdId: string) => previewPanelRef.current?.scrollToDsdId(dsdId),
    [],
  );
  // 줄을 빠르게 넘기면 앞 줄의 정렬 예약이 뒤 줄보다 늦게 끝날 수 있다 — 마지막 것만 쓴다.
  const showLineTokenRef = useRef(0);
  const showLineOnIxd = useCallback(
    (dsdIds: string[], anchorDsdId?: string) => {
      const token = ++showLineTokenRef.current;
      const panel = previewPanelRef.current;
      const container = panel?.getScrollContainer();
      if (!anchorDsdId || !panel || !container) {
        onDsdCellIdsChange(dsdIds);
        return;
      }
      // DSD 줄이 멈춘 높이에 대응 IXD 칸을 맞춘다 — 비교할 때 시선이 수평으로만 움직인다.
      afterScrollSettles(container, () => {
        if (token !== showLineTokenRef.current) return;
        onDsdCellIdsChange(dsdIds, {
          alignWith: panel.getCellElement(anchorDsdId),
        });
      });
    },
    [onDsdCellIdsChange],
  );
  const dlViewIdByDsdId = useMemo(
    () =>
      new Map(
        (dlRegions ?? []).flatMap((region) =>
          region.dsdIds.map((dsdId) => [dsdId, region.dlViewId] as const),
        ),
      ),
    [dlRegions],
  );
  const positionCheck = usePositionCheck({
    subChunks: subChunksForReview,
    factLabels: counterpartFactLabels,
    dlViewIdByDsdId,
    scrollToCell: scrollDsdToCell,
    onShowLine: showLineOnIxd,
  });
  const valuePresence = useMemo(
    () => summarizeValuePresence(subChunksForReview),
    [subChunksForReview],
  );
  const {
    summary: positionSummary,
    active: positionActive,
    current: positionCurrent,
    highlighted: positionHighlighted,
    jumpTo: jumpToPositionLine,
    clearHighlight: clearPositionHighlight,
    cursor: positionCursor,
    prev: prevPositionLine,
    next: nextPositionLine,
  } = positionCheck;
  const positionTextOf = useCallback(
    (dsdId: string) => positionSummary?.textByDsdId.get(dsdId) ?? "",
    [positionSummary],
  );
  const positionCard = useMemo(
    () =>
      positionCurrent && positionSummary ? (
        <PositionReviewCard
          line={positionCurrent}
          regionTitle={
            positionCurrent.inMultiRegionTable
              ? positionCurrent.counterpart.dlViewIds
                  .map(
                    (id) =>
                      dlRegions?.find((region) => region.dlViewId === id)
                        ?.title ?? "DL 미배정 칸",
                  )
                  .join(" · ")
              : null
          }
          cursor={positionCursor}
          total={positionSummary.flagged.length}
          headerTextOf={positionTextOf}
          onPrev={prevPositionLine}
          onNext={nextPositionLine}
        />
      ) : null,
    [
      dlRegions,
      positionCurrent,
      positionSummary,
      positionCursor,
      positionTextOf,
      prevPositionLine,
      nextPositionLine,
    ],
  );
  const positionReview = useMemo<DsdPositionReview | null>(
    () =>
      positionActive && positionSummary
        ? {
            summary: positionSummary,
            currentKey: positionCurrent?.key ?? null,
            highlighted: positionHighlighted,
            onJump: jumpToPositionLine,
            onClearHighlight: clearPositionHighlight,
            currentCard: positionCard,
          }
        : null,
    [
      positionCard,
      positionActive,
      positionSummary,
      positionCurrent,
      positionHighlighted,
      jumpToPositionLine,
      clearPositionHighlight,
    ],
  );

  // 헤더 태그 한글 라벨: IXD editor preview 의 th 헤더 텍스트로 만든
  // element_id → 한글명 맵(우클릭 태그 popover 라벨 소스).
  const ixdLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!editorPreview) return map;
    const allChunks = [
      ...(editorPreview.consolidatedChunks ?? []),
      ...(editorPreview.separateChunks ?? []),
    ];
    for (const chunk of allChunks) {
      for (const dlView of chunk.dlViews) {
        if (!dlView.table) continue;
        for (const rows of Object.values(dlView.table)) {
          for (const [id, label] of buildTableLabelMap(rows)) {
            if (!map.has(id)) map.set(id, label);
          }
        }
      }
    }
    return map;
  }, [editorPreview]);

  // 헤더 태그의 lineItem / member 를 한글 라벨로 채운다. 라벨이 없으면 raw id 유지.
  const labeledHeaderTags = useMemo(() => {
    if (!headerTagsByDsdId || ixdLabelMap.size === 0) return headerTagsByDsdId;
    const out = new Map<string, DsdHeaderTag>();
    for (const [dsdId, tag] of headerTagsByDsdId) {
      const key = tag.lineItem || tag.member;
      const label = key ? ixdLabelMap.get(key) : undefined;
      out.set(dsdId, label ? { ...tag, label } : tag);
    }
    return out;
  }, [headerTagsByDsdId, ixdLabelMap]);

  // 우클릭한 데이터 셀 → 백엔드가 준 inferredCellId 로 IXD 셀을 포커싱한다.
  // 매칭 클릭과 달리 배정된 DL 밖으로 나가지 않는다(navigation 훅이 소유하는 규칙).
  const handleInferredFocus = navigation.handleInferredCellFocus;

  const handleCompareFocusClose =
    onCompareFocusClose ?? navigation.handleNavigationClose;
  const handleIxdEditStart = navigation.handleNavigationDismiss;

  const activeIxdChunkId = useIxdDsdCompareStore((s) => s.activeIxdChunkId);

  // 현재 보이는 IXD chunk(activeIxdChunkId)에 속하는 셀들의 정규화된 cellId 집합.
  // editorPreview의 dlView.values에서 추출해 현재 화면/캐러셀 범위만 카운트한다.
  const activeChunkCellIds = useMemo(() => {
    if (!activeIxdChunkId || !editorPreview) return null;
    const allChunks = [
      ...(editorPreview.consolidatedChunks ?? []),
      ...(editorPreview.separateChunks ?? []),
    ];
    const chunk = allChunks.find((c) => c.id === activeIxdChunkId);
    if (!chunk) return null;
    const ids = new Set<string>();
    for (const dlView of chunk.dlViews) {
      if (dlView.table) {
        for (const rows of Object.values(dlView.table)) {
          for (const row of rows) {
            for (const column of row.columns) {
              if (isValueCell(column)) ids.add(normalizeCellId(column.id));
            }
          }
        }
      }
      if (!dlView.values) continue;
      for (const [periodType, tvs] of Object.entries(dlView.values)) {
        for (const tv of tvs) {
          for (const cellId of buildCellIdAliasesFromTaggedValue(
            periodType,
            tv,
          )) {
            ids.add(cellId);
          }
        }
      }
    }
    return ids;
  }, [activeIxdChunkId, editorPreview]);

  // 활성 chunk에 속한 변경 개수. editSession.cellChanges는 현재 fsType 슬라이스이고
  // activeChunkCellIds는 normalized cellId 집합이다. store는 raw cellId를 보존하므로
  // 같은 셀이 axis 순서만 다른 raw 키로 두 번 들어가는 케이스(드뭄)에서도 셀 단위로
  // 한 번만 카운트되도록 normalized 기준 Set으로 dedupe.
  const unsavedCountActiveChunk = useMemo(() => {
    if (!activeChunkCellIds) return 0;
    const counted = new Set<string>();
    for (const cellId of Object.keys(editSession.cellChanges)) {
      const normalized = normalizeCellId(cellId);
      if (activeChunkCellIds.has(normalized)) counted.add(normalized);
    }
    return counted.size;
  }, [activeChunkCellIds, editSession.cellChanges]);

  return (
    <div className="relative flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden">
      <CompareHeader
        memberAxes={memberAxes}
        reviewAxisId={reviewAxisId}
        onReviewAxisChange={setReviewAxisId}
        dsdFile={
          dsdFile ? { name: dsdFile.name, uploadedAt: new Date() } : null
        }
        ixdFile={
          ixdFile ? { name: ixdFile.name, uploadedAt: new Date() } : null
        }
        canEdit={canEdit}
        editDisabledReason={editDisabledReason}
        isEditMode={isIxdEditMode}
        onEditModeChange={setIsIxdEditMode}
        hasUnsavedChanges={editSession.hasUnsavedChanges}
        unsavedCountActiveChunk={unsavedCountActiveChunk}
        unsavedCountTotal={editSession.unsavedCounts.total}
        showRawValueInEdit={editSession.showRawValueInEdit}
        onToggleRawValue={editSession.toggleShowRawValueInEdit}
        onResetAllChanges={editSession.resetAllChanges}
        onSave={() => editSession.saveMutation.mutate()}
        onDownload={() => editSession.downloadMutation.mutate()}
        isActionLocked={editSession.isActionLocked}
        saveInFlight={editSession.saveInFlight}
        downloadInFlight={editSession.downloadInFlight}
        isReflecting={editSession.isReflecting}
        onDownloadHtml={onDownloadHtml}
        isDownloadingHtml={isDownloadingHtml}
        reviewToolSlot={
          <BetaFeatureOnly>
            <PositionReviewToggle
              available={!!positionSummary}
              active={positionCheck.active}
              flaggedCount={positionSummary?.flagged.length ?? 0}
              presence={valuePresence}
              showNewBadge={positionCheck.showNewBadge}
              onToggle={
                positionCheck.active
                  ? positionCheck.deactivate
                  : positionCheck.activate
              }
              statusCard={
                positionCheck.active && positionSummary && !positionCurrent ? (
                  <PositionReviewStatus
                    total={positionSummary.flagged.length}
                    finished={positionCheck.finished}
                    onNext={positionCheck.next}
                    onPrev={positionCheck.prev}
                  />
                ) : null
              }
            />
          </BetaFeatureOnly>
        }
      />

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          {isChunkLoading ? (
            <div className="flex items-center justify-center flex-1 bg-background-neutral-alternative">
              <ChunkLoadingIndicator label="데이터를 불러오는 중..." />
            </div>
          ) : (
            <div
              className="relative flex gap-5 flex-1 min-h-0 overflow-hidden px-3 p-5"
              {...(positionCheck.active && {
                onMouseDownCapture: blockWhileReviewing,
                onClickCapture: blockWhileReviewing,
                onDoubleClickCapture: blockWhileReviewing,
                onContextMenuCapture: blockWhileReviewing,
              })}
            >
              {isChunkFetching && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-background-neutral-alternative/60 backdrop-blur-[1px]">
                  <ChunkLoadingIndicator label="불러오는 중..." />
                </div>
              )}
              <PreviewPanel
                ref={previewPanelRef}
                activeChunk={activeChunk}
                focusedDsdResetKey={focusedDsdResetKey}
                onValueClick={navigation.handleMatchedValueClick}
                onShouldTagCellClick={navigation.handleShouldTagCellClick}
                onNoMatchCellClick={navigation.handleNavigationClose}
                counterpartDsdIds={counterpartDsdIds}
                headerTagsByDsdId={labeledHeaderTags}
                dlRegions={dlRegions}
                periodRegions={periodRegions}
                lineItemRegions={lineItemRegions}
                memberRegions={memberRegions}
                reviewAxisId={reviewAxisId}
                positionReview={positionReview}
                ixdLabelMap={ixdLabelMap}
                onInferredFocus={handleInferredFocus}
                onHeaderFocus={navigation.handleHeaderFocus}
                onSelectedDsdCellIdsChange={onDsdCellIdsChange}
              />

              <IxdPreviewPanel
                ixdRendererRef={ixdRendererRef}
                activeChunkId={activeChunk?.id || null}
                onClearFocus={handleCompareFocusClose}
                currentIxdChunks={currentIxdChunks}
                activeIxdChunk={activeIxdChunk}
                editable={editable}
                cellChanges={editSession.cellChanges}
                changeOrigins={editSession.changeOrigins}
                cellIdentityByNormalizedCellId={
                  editSession.cellIdentityByNormalizedCellId
                }
                identityChanges={editSession.identityChanges}
                identityLocationsByKey={editSession.identityLocationsByKey}
                chunkFsTypeById={editSession.chunkFsTypeById}
                onCellEdit={editSession.setCellValue}
                onCellEditStart={handleIxdEditStart}
                onCellEditBatch={editSession.setCellValues}
                onFocusIdentityLocation={onFocusIdentityLocation}
                showRawValueInEdit={editSession.showRawValueInEdit}
                onUndo={editSession.undo}
                onSelectedCellIdsChange={onIxdCellIdsChange}
                counterpartCellIds={ixdCounterpart.cellIds}
                counterpartIdentityKeys={ixdCounterpart.identityKeys}
                isLinkageLoading={isLinkageLoading}
                matchedCellIds={matchedIxdCellIds}
                showUnmatchedStatus={!editable}
              />
            </div>
          )}
        </div>
      </div>

      {/* 매칭 셀 navigation — 포커싱된 IXD 셀 옆에 anchor 되어 매칭 셀들 사이 이동 */}
      {matchNavigation && matchNavigation.matchingCells.length > 0 && (
        <MatchNavigationPopover
          matchNavigation={matchNavigation}
          onNavigateToIndex={navigation.handleNavigateToIndex}
          onClose={handleCompareFocusClose}
        />
      )}

      {/* Busy overlay (저장/다운로드 진행 중) */}
      {editSession.isBusy && (
        <div
          className="absolute inset-0 z-50 flex items-center justify-center bg-static-black/20 backdrop-blur-[1px] pointer-events-auto cursor-wait"
          aria-busy="true"
        >
          <div className="flex items-center gap-3 rounded-[10px] border border-line-normal bg-background-base px-5 py-3 shadow-xl">
            <Loader2 className="w-5 h-5 animate-spin text-primary-base" />
            <div className="flex flex-col">
              <span className="label-1-normal-medium text-label-normal">
                {editSession.downloadInFlight ? "다운로드 중..." : "저장 중..."}
              </span>
              <span className="caption-2-regular text-label-alternative">
                {editSession.downloadInFlight
                  ? "수정된 IXD 파일을 준비하고 있습니다"
                  : "수정 내역을 저장하고 있습니다"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
